import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import AdminLayout from "../../components/AdminLayout";
import { toast } from "sonner";
import axios from "axios";
import { 
  Zap, Play, RefreshCw, Globe, Sparkles, CheckCircle, Clock,
  Image as ImageIcon, Search, Loader2, AlertCircle, FileText,
  ThumbsUp, Trash2, Eye, ChevronDown, ChevronUp, Star
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Automation() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [updatingImages, setUpdatingImages] = useState(false);
  const [searchingArtist, setSearchingArtist] = useState("");
  const [artistName, setArtistName] = useState("");
  const [pendingArticles, setPendingArticles] = useState([]);
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem("token");
  const authHeaders = { Authorization: `Bearer ${getToken()}` };

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/automation/status`, { headers: authHeaders });
      setStatus(response.data);
    } catch (error) {
      console.error("Error fetching status:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPending = async () => {
    try {
      const response = await axios.get(`${API}/articles/pending`, { headers: authHeaders });
      setPendingArticles(response.data);
    } catch (error) {
      console.error("Error fetching pending:", error);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchPending();
  }, []);

  const runAutomation = async () => {
    setRunning(true);
    try {
      const response = await axios.post(`${API}/automation/run`, {}, {
        headers: authHeaders, timeout: 120000
      });
      toast.success(response.data.message);
      fetchStatus();
      fetchPending();
    } catch (error) {
      toast.error("Erro ao executar automação");
    } finally {
      setRunning(false);
    }
  };

  const updateAllImages = async () => {
    setUpdatingImages(true);
    toast.info("Buscando imagens reais...");
    try {
      const response = await axios.post(`${API}/automation/update-all-images`, {}, {
        headers: authHeaders, timeout: 300000
      });
      if (response.data.updated?.length > 0) {
        toast.success(`${response.data.updated.length} artistas atualizados!`);
      } else {
        toast.info("Nenhuma nova imagem encontrada");
      }
    } catch (error) {
      toast.error("Erro ao atualizar imagens");
    } finally {
      setUpdatingImages(false);
    }
  };

  const searchArtistImage = async () => {
    if (!artistName.trim()) return;
    setSearchingArtist(artistName);
    try {
      const response = await axios.post(`${API}/automation/search-artist-image`, 
        { artist_name: artistName },
        { headers: authHeaders, timeout: 60000 }
      );
      if (response.data.success) {
        toast.success(`Imagem encontrada para ${response.data.artist}!`);
        setArtistName("");
      } else {
        toast.error("Imagem não encontrada");
      }
    } catch (error) {
      toast.error("Erro ao buscar imagem");
    } finally {
      setSearchingArtist("");
    }
  };

  const approveArticle = async (articleId) => {
    setApprovingId(articleId);
    try {
      await axios.post(`${API}/articles/${articleId}/approve`, {}, { headers: authHeaders });
      toast.success("Matéria aprovada e publicada!");
      setPendingArticles(prev => prev.filter(a => a.id !== articleId));
      fetchStatus();
    } catch (error) {
      toast.error("Erro ao aprovar matéria");
    } finally {
      setApprovingId(null);
    }
  };

  const rejectArticle = async (articleId) => {
    if (!window.confirm("Tem certeza que deseja rejeitar esta matéria? Ela será removida.")) return;
    try {
      await axios.post(`${API}/articles/${articleId}/reject`, {}, { headers: authHeaders });
      toast.success("Matéria rejeitada e removida.");
      setPendingArticles(prev => prev.filter(a => a.id !== articleId));
      fetchStatus();
    } catch (error) {
      toast.error("Erro ao rejeitar matéria");
    }
  };

  const toggleFeatured = async (article) => {
    try {
      const res = await axios.post(`${API}/articles/${article.id}/feature`, {}, { headers: authHeaders });
      const isFeatured = res.data?.is_featured;
      toast.success(isFeatured ? "⭐ Marcada como destaque!" : "Removida dos destaques");
      setPendingArticles(prev => prev.map(a => a.id === article.id ? { ...a, is_featured: isFeatured } : a));
    } catch (error) {
      console.error("Featured error", error);
      toast.error("Erro ao marcar destaque");
    }
  };

  const approveAll = async () => {
    if (!window.confirm(`Aprovar todas as ${pendingArticles.length} matérias pendentes?`)) return;
    for (const article of pendingArticles) {
      try {
        await axios.post(`${API}/articles/${article.id}/approve`, {}, { headers: authHeaders });
      } catch (e) {
        console.error(`Erro ao aprovar artigo ${article.id}:`, e);
      }
    }
    toast.success(`${pendingArticles.length} matérias aprovadas!`);
    setPendingArticles([]);
    fetchStatus();
  };

  const rejectAll = async () => {
    if (!window.confirm(`DESCARTAR TODAS as ${pendingArticles.length} matérias pendentes? Esta ação não pode ser desfeita.`)) return;
    const total = pendingArticles.length;
    let deleted = 0;
    for (const article of pendingArticles) {
      try {
        await axios.post(`${API}/articles/${article.id}/reject`, {}, { headers: authHeaders });
        deleted += 1;
      } catch (e) {
        console.error(`Erro ao rejeitar artigo ${article.id}:`, e);
      }
    }
    toast.success(`${deleted}/${total} matérias descartadas`);
    setPendingArticles([]);
    fetchStatus();
  };

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF007F] to-[#00E5FF] flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white" data-testid="automation-title">
              Central de Automação
            </h1>
            <p className="text-zinc-500">IA escaneia, reescreve e você aprova</p>
          </div>
        </div>

        {/* Pending Articles - APPROVAL PANEL */}
        <div className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 rounded-2xl border border-amber-500/30 overflow-hidden">
          <div className="p-5 border-b border-amber-500/20 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="text-amber-500" size={22} />
              <h2 className="font-heading text-xl font-bold text-white">
                Matérias Pendentes de Aprovação
              </h2>
              {pendingArticles.length > 0 && (
                <span className="bg-amber-500 text-black text-xs font-bold px-2.5 py-1 rounded-full">
                  {pendingArticles.length}
                </span>
              )}
            </div>
            {pendingArticles.length > 1 && (
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={approveAll}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white text-sm font-bold rounded-lg transition-all"
                  data-testid="approve-all-btn"
                >
                  <ThumbsUp size={16} />
                  Aprovar Todas
                </button>
                <button
                  onClick={rejectAll}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white text-sm font-bold rounded-lg transition-all border border-red-600/30"
                  data-testid="reject-all-btn"
                >
                  <Trash2 size={16} />
                  Descartar Todas
                </button>
              </div>
            )}
          </div>

          {pendingArticles.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircle className="w-12 h-12 text-green-500/50 mx-auto mb-3" />
              <p className="text-zinc-400">Nenhuma matéria pendente. Execute a automação para gerar novas matérias.</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {pendingArticles.map((article) => (
                <div key={article.id} className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Thumbnail */}
                    {article.image_url && (
                      <img
                        src={article.image_url}
                        alt=""
                        className="w-20 h-20 rounded-lg object-cover object-top flex-shrink-0"
                      />
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="bg-amber-500/20 text-amber-400 text-xs font-bold px-2 py-0.5 rounded">
                          RASCUNHO
                        </span>
                        <span className="bg-white/10 text-zinc-400 text-xs px-2 py-0.5 rounded">
                          {article.category}
                        </span>
                        {article.is_ai_generated && (
                          <span className="bg-[#FF007F]/20 text-[#FF007F] text-xs px-2 py-0.5 rounded flex items-center gap-1 font-semibold">
                            Equipe Arrocha Play
                          </span>
                        )}
                        {article.image_origin === "source" && (
                          <span className="bg-green-500/15 text-green-400 text-xs px-2 py-0.5 rounded font-semibold" title="Foto vinda da matéria original (verificada pela fonte)">
                            📷 Foto da fonte
                          </span>
                        )}
                        {article.image_origin === "bank" && (
                          <span className="bg-blue-500/15 text-blue-400 text-xs px-2 py-0.5 rounded font-semibold" title="Foto do banco interno verificado">
                            📷 Banco interno
                          </span>
                        )}
                        {article.image_origin === "none" && (
                          <span className="bg-red-500/15 text-red-400 text-xs px-2 py-0.5 rounded font-semibold" title="Sem foto - você precisará adicionar antes de aprovar">
                            ⚠️ Sem foto
                          </span>
                        )}
                        {article.source_name && (
                          <span className="text-zinc-600 text-xs">
                            via {article.source_name}
                          </span>
                        )}
                      </div>
                      
                      <h3 className="text-white font-medium leading-tight mb-1">{article.title}</h3>
                      <p className="text-zinc-500 text-sm line-clamp-2">{article.excerpt}</p>
                      
                      {/* Expanded content */}
                      {expandedArticle === article.id && (
                        <div className="mt-3 p-4 bg-[#0A0A0A] rounded-lg border border-white/5">
                          <div 
                            className="text-zinc-300 text-sm leading-relaxed prose prose-invert max-w-none"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
                          />
                          {article.artist_name && (
                            <p className="text-zinc-500 text-xs mt-3">Artista: {article.artist_name}</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => setExpandedArticle(expandedArticle === article.id ? null : article.id)}
                        className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400 transition-all"
                        title="Ver conteúdo"
                        data-testid={`preview-${article.id}`}
                      >
                        {expandedArticle === article.id ? <ChevronUp size={18} /> : <Eye size={18} />}
                      </button>
                      <button
                        onClick={() => navigate(`/admin/articles/edit/${article.id}`)}
                        className="p-2 rounded-lg bg-[#00E5FF]/20 hover:bg-[#00E5FF]/40 text-[#00E5FF] transition-all"
                        title="Editar com agente IA"
                        data-testid={`edit-${article.id}`}
                      >
                        <Search size={18} />
                      </button>
                      <button
                        onClick={() => toggleFeatured(article)}
                        className={`p-2 rounded-lg transition-all ${
                          article.is_featured
                            ? "bg-amber-500/25 text-amber-300 hover:bg-amber-500/40"
                            : "bg-white/5 hover:bg-amber-500/15 text-zinc-400 hover:text-amber-400"
                        }`}
                        title={article.is_featured ? "Remover destaque" : "Marcar como destaque"}
                        data-testid={`feature-${article.id}`}
                      >
                        <Star size={18} fill={article.is_featured ? "currentColor" : "none"} />
                      </button>
                      <button
                        onClick={() => approveArticle(article.id)}
                        disabled={approvingId === article.id}
                        className="p-2 rounded-lg bg-green-600/20 hover:bg-green-600/40 text-green-400 transition-all disabled:opacity-50"
                        title="Aprovar e publicar"
                        data-testid={`approve-${article.id}`}
                      >
                        {approvingId === article.id ? (
                          <Loader2 className="animate-spin" size={18} />
                        ) : (
                          <ThumbsUp size={18} />
                        )}
                      </button>
                      <button
                        onClick={() => rejectArticle(article.id)}
                        className="p-2 rounded-lg bg-red-600/20 hover:bg-red-600/40 text-red-400 transition-all"
                        title="Rejeitar e remover"
                        data-testid={`reject-${article.id}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Run Automation */}
          <div className="bg-gradient-to-br from-[#FF007F]/20 to-[#FF007F]/5 rounded-2xl p-6 border border-[#FF007F]/30">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#FF007F]/30 flex items-center justify-center flex-shrink-0">
                <Globe className="w-6 h-6 text-[#FF007F]" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-white mb-1">
                  Escanear Notícias
                </h3>
                <p className="text-zinc-400 text-sm">
                  Escaneia Google News por matérias de arrocha, reescreve com IA e salva como rascunho.
                </p>
              </div>
            </div>
            <button
              onClick={runAutomation}
              disabled={running}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-[#FF007F] hover:bg-[#FF3399] disabled:opacity-50 text-white font-bold rounded-xl transition-all"
              data-testid="run-automation-btn"
            >
              {running ? (
                <><Loader2 className="animate-spin" size={20} /> Escaneando...</>
              ) : (
                <><Play size={20} /> Executar Agora</>
              )}
            </button>
          </div>

          {/* Update Images */}
          <div className="bg-gradient-to-br from-[#00E5FF]/20 to-[#00E5FF]/5 rounded-2xl p-6 border border-[#00E5FF]/30">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#00E5FF]/30 flex items-center justify-center flex-shrink-0">
                <ImageIcon className="w-6 h-6 text-[#00E5FF]" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-white mb-1">
                  Atualizar Fotos
                </h3>
                <p className="text-zinc-400 text-sm">
                  Busca fotos reais de todos os artistas via Deezer e salva permanentemente.
                </p>
              </div>
            </div>
            <button
              onClick={updateAllImages}
              disabled={updatingImages}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-[#00E5FF] hover:bg-[#00E5FF]/80 disabled:opacity-50 text-black font-bold rounded-xl transition-all"
              data-testid="update-images-btn"
            >
              {updatingImages ? (
                <><Loader2 className="animate-spin" size={20} /> Buscando...</>
              ) : (
                <><RefreshCw size={20} /> Atualizar Todas</>
              )}
            </button>
          </div>
        </div>

        {/* Search Specific Artist */}
        <div className="bg-[#121212] rounded-2xl p-6 border border-white/10">
          <h3 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Search className="text-[#FFB800]" size={20} />
            Buscar Foto de Artista
          </h3>
          <div className="flex gap-4">
            <input
              type="text"
              value={artistName}
              onChange={(e) => setArtistName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchArtistImage()}
              placeholder="Ex: Pablo, Silvanno Salles, Natanzinho Lima"
              className="flex-1 px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-[#FFB800]"
              data-testid="artist-search-input"
            />
            <button
              onClick={searchArtistImage}
              disabled={!!searchingArtist}
              className="flex items-center gap-2 px-6 py-3 bg-[#FFB800] hover:bg-[#FFB800]/80 disabled:opacity-50 text-black font-bold rounded-xl transition-all"
              data-testid="search-artist-btn"
            >
              {searchingArtist ? <Loader2 className="animate-spin" size={20} /> : <Search size={20} />}
              Buscar
            </button>
          </div>
        </div>

        {/* Stats */}
        {!loading && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-[#121212] rounded-2xl p-5 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <Globe className="text-[#FF007F]" size={20} />
                <span className="text-zinc-500 text-sm">Escaneados</span>
              </div>
              <p className="text-2xl font-bold text-white">{status?.total_scraped || 0}</p>
            </div>
            <div className="bg-[#121212] rounded-2xl p-5 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="text-amber-500" size={20} />
                <span className="text-zinc-500 text-sm">Rascunhos</span>
              </div>
              <p className="text-2xl font-bold text-white">{status?.total_drafts || 0}</p>
            </div>
            <div className="bg-[#121212] rounded-2xl p-5 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="text-green-500" size={20} />
                <span className="text-zinc-500 text-sm">Publicados</span>
              </div>
              <p className="text-2xl font-bold text-white">{status?.total_published || 0}</p>
            </div>
            <div className="bg-[#121212] rounded-2xl p-5 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="text-[#00E5FF]" size={20} />
                <span className="text-zinc-500 text-sm">Matérias IA</span>
              </div>
              <p className="text-2xl font-bold text-white">{status?.total_ai_articles || 0}</p>
            </div>
          </div>
        )}

        {/* How it works */}
        <div className="bg-[#0A0A0A] rounded-2xl p-6 border border-white/5">
          <h3 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
            <AlertCircle className="text-[#FFB800]" size={20} />
            Como funciona
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { n: "1", color: "#FF007F", title: "Scraping", desc: "Escaneia Google News" },
              { n: "2", color: "#00E5FF", title: "Reescrita IA", desc: "Claude reescreve" },
              { n: "3", color: "#FFB800", title: "Foto Real", desc: "Busca foto do artista" },
              { n: "4", color: "#10B981", title: "Aprovação", desc: "Admin aprova e publica" },
            ].map((step) => (
              <div key={step.n} className="text-center p-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2" style={{ backgroundColor: `${step.color}20` }}>
                  <span style={{ color: step.color }} className="font-bold text-sm">{step.n}</span>
                </div>
                <h4 className="text-white font-medium text-sm mb-0.5">{step.title}</h4>
                <p className="text-zinc-500 text-xs">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
